import { Text } from '@chakra-ui/react';
import styled from '@emotion/styled';
// eslint-disable-next-line no-restricted-imports
import { Trans } from '@lingui/react';
import Box from '@material-ui/core/Box';
import { ReactComponent as DiscountSVG } from 'assets_2/icons/memberplus.svg';
import Dot from 'components/Dot';
import BaseText from 'components_2/textStyles/BaseText';
import dayjs from 'dayjs';
import { dateIsThisYear } from 'helpers/datetime';
import getFormattedAmount from 'helpers/getFormattedAmount';
import sumBy from 'lodash/sumBy';
import React from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { getUserImageUrl, getWorkshopImageUrl } from 'services/media';
import { ReactComponent as GraduationCap } from '../../Assets/images/icons/graudation-cap.svg';
import { ShopProductSummary } from './useFeaturedWorkshopList';
import WorkshopPrice from './WorkshopPrice';
import WorkshopTileTag from './WorkshopTileTag';

interface WorkshopItemTileProps {
  workshop: ShopProductSummary;
  showAvailableSeats?: boolean;
  showDiscount?: boolean;
  discount?: number;
  showTimeOnTag?: boolean;
  showPrice?: boolean;
  pastWorkshopIndicator?: boolean;
  toLink?: string;
  showTag?: boolean;
  showDescription?: boolean;
  showDateAndTime?: boolean;
}

const WorkshopImage = styled.img`
  width: 100%;
  object-fit: cover;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ShortDescriptionText = styled(BaseText)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const getRelativeTime = (date: number) => {
  return dayjs(date).format(
    dateIsThisYear(date) ? `DD MMM · hh:mmA` : `DD MMM YYYY · hh:mmA`
  );
};
const getRelativeDate = (date: number) => {
  return dayjs(date).format(dateIsThisYear(date) ? `DD MMM` : `DD MMM YYYY`);
};

function beforeDiscountPrice(price: number, currency: string) {
  return (
    <BaseText textStyle="caption1" color="var(--red-400)">
      WAS{' '}
      <span className="crossed">
        {getFormattedAmount({ amount: price, currency })}
      </span>
    </BaseText>
  );
}

export const DiscountBadge = () => {
  return (
    <Box display="flex" maxWidth="155px" paddingTop="2px" alignItems="center">
      <DiscountSVG width="100%" alt="memberplus-discount" />
      <Box width="5px" />
      <Box
        paddingLeft="5px"
        borderLeft="1px solid #EFF3F3"
        display="flex"
        paddingTop="2.5px"
      >
        <BaseText textStyle="caption1" color="var(--yellow-400)">
          DISCOUNT
        </BaseText>
      </Box>
    </Box>
  );
};

const WorkshopItemTile = ({
  workshop,
  showAvailableSeats,
  showDiscount,
  discount = 1,
  showTimeOnTag = true,
  showPrice = true,
  pastWorkshopIndicator = false,
  toLink,
  showTag = true,
  showDateAndTime = true,
  showDescription = true
}: WorkshopItemTileProps) => {
  const {
    title,
    owner,
    id,
    prices,
    start_date,
    maximum_number_of_attendees,
    number_of_sales,
    price_text
  } = workshop ?? {};
  const workshopImage = getWorkshopImageUrl(
    id,
    owner.id,
    'rectangle',
    'medium'
  );
  const totalPrice = sumBy(prices, priceDetails => priceDetails?.price ?? 0);
  const priceDiscounted = totalPrice * (1 - discount);
  const currency = prices?.[0]?.currency;
  const isPaid = totalPrice > 0;
  const numberOfAvailableSeats =
    (maximum_number_of_attendees ?? 0) - (number_of_sales ?? 0);
  const shouldShowSpotsLeft =
    showAvailableSeats &&
    numberOfAvailableSeats < 11 &&
    numberOfAvailableSeats >= 0;
  const isPastWorkshop = dayjs(start_date?.epoch).isBefore(new Date());
  const showPastWorkshopIndicator = isPastWorkshop && pastWorkshopIndicator;
  const isWorkshopRecording = workshop.type === 'WORKSHOP_RECORDING';

  const location = useLocation();
  const appendQuery = (path: string) => {
    return path + location.search;
  };
  const workshopPath = `/${owner.username}/workshops/${id}`;
  const path = showDiscount ? appendQuery(workshopPath) : workshopPath;

  return (
    <Box paddingTop="8px">
      <Link to={toLink ?? path}>
        <Box
          className="animate animate__carousel-tile-pop tile_background_image"
          width="100%"
          paddingTop="61%"
          position="relative"
          borderRadius="12px"
          overflow="hidden"
          style={
            showPastWorkshopIndicator
              ? { filter: 'grayscale(100%)' }
              : undefined
          }
        >
          <WorkshopImage
            src={workshopImage}
            onError={e => {
              const userImage = getUserImageUrl(owner.id, 'square', 'medium');
              (e.target as HTMLImageElement).src = userImage;
            }}
            alt={`${title} workshop`}
          />

          <Box
            display="flex"
            position="absolute"
            left="8px"
            bottom="8px"
            alignItems="start"
          >
            {showTag && (
              <WorkshopTileTag
                padding="2px 5px 2px 3px"
                bgcolor={
                  showPastWorkshopIndicator
                    ? 'var(--neutral-0)'
                    : 'var(--blue-400)'
                }
              >
                <Box marginTop="-1px">
                  <GraduationCap
                    width="10px"
                    height="9px"
                    fill={
                      showPastWorkshopIndicator ? '#DADADA' : 'var(--neutral-0)'
                    }
                  />
                </Box>
                <Box width="5px" />
                <Text
                  fontSize="11px"
                  lineHeight="15px"
                  fontWeight={700}
                  color={
                    showPastWorkshopIndicator
                      ? 'var(--neutral-500)'
                      : 'var(--neutral-0)'
                  }
                  letterSpacing="-0.01em"
                >
                  {isWorkshopRecording ? 'ON-DEMAND' : 'WORKSHOP'}
                </Text>
              </WorkshopTileTag>
            )}

            {!isWorkshopRecording && showDateAndTime && (
              <>
                <Box width="6px" />

                <WorkshopTileTag
                  bgcolor="var(--neutral-0)"
                  padding="2px 5px 2px 3px"
                >
                  <Text
                    fontSize="11px"
                    lineHeight="15px"
                    fontWeight={700}
                    color={
                      showPastWorkshopIndicator
                        ? 'var(--neutral-500)'
                        : 'var(--neutral-700)'
                    }
                    letterSpacing="-0.01em"
                  >
                    {showTimeOnTag
                      ? getRelativeTime(start_date?.epoch).toUpperCase()
                      : getRelativeDate(start_date?.epoch).toUpperCase()}
                  </Text>
                </WorkshopTileTag>
              </>
            )}
          </Box>
        </Box>
        <Box height="14px" />
        <Box display="flex" alignItems="space-between">
          <Box marginRight="5px" flexGrow={1} flexBasis="10px">
            <BaseText
              textStyle="subHeadingMedium"
              color="var(--neutral-700)"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {title}
            </BaseText>
            <Box height="6px" />
            <Box display="flex" alignItems="center" color="var(--neutral-500)">
              <BaseText textStyle="p2Medium" color="var(--neutral-500)">
                <Trans id="with" />
                &nbsp;
                {owner?.name}
              </BaseText>
              {price_text ? (
                <>
                  <Dot marginLeft="5px" marginRight="5px" />
                  <BaseText textStyle="p2Medium" color="var(--neutral-500)">
                    {price_text}
                  </BaseText>
                </>
              ) : null}
            </Box>
          </Box>
          {showPrice && (
            <Box textAlign="right">
              <Box
                flexShrink={0}
                display="flex"
                justifyContent="flex-end"
                alignItems="baseline"
              >
                {isPaid ? (
                  <WorkshopPrice
                    price={showDiscount ? priceDiscounted : totalPrice}
                    currency={currency}
                  />
                ) : (
                  <BaseText
                    textStyle="subHeadingMedium"
                    color="var(--neutral-700)"
                  >
                    <Trans id="Free" />
                  </BaseText>
                )}
                {isPaid && showDiscount && totalPrice && (
                  <>
                    <Box width="4px" />
                    {beforeDiscountPrice(totalPrice, currency)}
                  </>
                )}
              </Box>
              <Box height="5px" />
              {showDiscount && (
                <Box marginLeft="5px">
                  <DiscountBadge />
                </Box>
              )}
              {!showDiscount && shouldShowSpotsLeft && (
                <Box marginLeft="5px" paddingTop="2px">
                  <BaseText textStyle="caption1" color="var(--red-400)">
                    {numberOfAvailableSeats === 0
                      ? 'SOLD OUT'
                      : `${numberOfAvailableSeats} ${
                          numberOfAvailableSeats === 1
                            ? 'SPOT LEFT'
                            : 'SPOTS LEFT'
                        }`}
                  </BaseText>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box height="12px" />
        {showDescription && (
          <ShortDescriptionText
            textStyle="p2Regular"
            color="var(--neutral-600)"
          >
            {workshop.short_description ?? workshop.description}
          </ShortDescriptionText>
        )}
      </Link>
    </Box>
  );
};

export default WorkshopItemTile;
